import { setScopedEntry } from "@frontend-shared/utils";
import { useCallback, useEffect, useRef } from "react";

import type { IAttachment } from "../types";
import { EAttachmentStatus } from "../types";
import { useChatContext } from "./useChatContext";

// NOTE: In-memory, per-session store of unsent attachments keyed by a scope key
// (see the caller's chatScopeKey). Retains the files a user has added to a chat's
// composer when they navigate between chats and back within the same session,
// without leaking one chat's attachments into another (NSAI-3912). Cleared on
// full page reload by design — attachments are never persisted to storage.
// Mirrors useChatDraft so the whole unsent draft (text + files) travels together.
// Entry count is capped (see setScopedEntry) so retained File/ArrayBuffer data
// from abandoned chats can't grow unbounded across a long session.
const chatAttachmentStore = new Map<string, IAttachment[]>();

// NOTE: NSAI-4876 — what survives the clear that runs after a send. FAILED did
// not ride the message and carries the retry the user was just told to use.
// UPLOADING is a retry started after that send and still in flight: it did not
// ride the message either, and dropping it removes the id its own upload writes
// its result back to, so the object reaches storage with no chip and no s3Key —
// the orphan this ticket exists to stop. Mirrors the ack-clear on mobile.
const isRetainedAfterSend = (attachment: IAttachment): boolean =>
  attachment.status === EAttachmentStatus.FAILED ||
  attachment.status === EAttachmentStatus.UPLOADING;

// NOTE: Clear all retained attachments. Called on sign-out so one user's files
// can't hydrate the next session (logout is a client-side navigate, not a
// reload, so this module-level store would otherwise survive).
export const resetChatAttachmentStore = (): void => {
  chatAttachmentStore.clear();
};

interface IMigrateAttachmentsProps {
  fromScopeKey: string;
  toScopeKey: string;
}

interface IUseChatAttachmentPersistenceReturn {
  clearSentAttachments: (scopeKey?: string) => void;
  migrateAttachments: (props: IMigrateAttachmentsProps) => void;
}

// NOTE: Scopes the shared ChatContext attachments to the active chat. The
// attachments themselves stay in the context (many components read them); this
// hook keeps a per-scope copy in sync and swaps which scope is loaded as the
// route changes. Only the main chat page needs this — other ChatContextProvider
// users (shared chat, assistant form) don't switch chats.
export const useChatAttachmentPersistence = (
  scopeKey: string,
): IUseChatAttachmentPersistenceReturn => {
  const { attachments, setAttachments } = useChatContext();
  const activeKeyRef = useRef<string>(scopeKey);

  // NOTE: On mount and whenever the active scope changes, hydrate the shared
  // context from that scope's stored attachments (empty if it has none).
  useEffect(() => {
    activeKeyRef.current = scopeKey;
    setAttachments(chatAttachmentStore.get(scopeKey) ?? []);
  }, [scopeKey, setAttachments]);

  // NOTE: Write-through — keep the store in sync with the active scope's
  // attachments on every change, so nothing is lost when the chat page unmounts
  // without a scope switch (drafts get this for free via setDraft on keystroke).
  useEffect(() => {
    if (attachments.length > 0) {
      setScopedEntry({
        store: chatAttachmentStore,
        key: activeKeyRef.current,
        value: attachments,
      });
    } else {
      chatAttachmentStore.delete(activeKeyRef.current);
    }
  }, [attachments]);

  // NOTE: Drops the attachments that rode the message, for a specific scope
  // (defaults to the active one). Callers that clear after an awaited step
  // (send-after-upload) pass the scope captured at call time so a mid-await chat
  // switch can't clear the wrong chat's files. The shared context is only reset
  // when the cleared scope is still active — otherwise the user has navigated on.
  //
  // NOTE: NSAI-4876 — a FAILED attachment did NOT ride the message, so it is
  // retained. Clearing it here contradicted the very toast this flow raises,
  // which tells the user to retry the file: the chip carrying that retry was
  // being removed in the same breath, and the file was lost with it.
  const clearSentAttachments = useCallback(
    (scopeKeyToClear?: string): void => {
      const key = scopeKeyToClear ?? activeKeyRef.current;

      // NOTE: For the active scope the filter runs against the state updater's
      // own `prev`, not a captured value: this is called straight after an
      // awaited upload, and the FAILED statuses that upload just wrote may not
      // have been committed yet. The write-through effect above then persists
      // whatever survives, so the store needs no separate write here.
      if (key === activeKeyRef.current) {
        setAttachments((prev) => prev.filter(isRetainedAfterSend));
        return;
      }

      const retained = (chatAttachmentStore.get(key) ?? []).filter(
        isRetainedAfterSend,
      );

      if (retained.length > 0) {
        setScopedEntry({ store: chatAttachmentStore, key, value: retained });
        return;
      }

      chatAttachmentStore.delete(key);
    },
    [setAttachments],
  );

  // NOTE: NSAI-4876 — mirrors `migrateChatDraft` (NSAI-3996). When the server
  // creates the chat mid-send, the scope key flips to the real id and the
  // hydrate effect above loads that new, empty scope — which would drop the
  // FAILED chip the send deliberately retained, on the single most common path
  // there is. Carry it across that one handoff, exactly as the draft text is.
  const migrateAttachments = useCallback(
    ({ fromScopeKey, toScopeKey }: IMigrateAttachmentsProps): void => {
      const carried = chatAttachmentStore.get(fromScopeKey) ?? [];
      chatAttachmentStore.delete(fromScopeKey);

      if (carried.length === 0) return;

      setScopedEntry({
        store: chatAttachmentStore,
        key: toScopeKey,
        value: carried,
      });

      if (toScopeKey === activeKeyRef.current) {
        setAttachments(carried);
      }
    },
    [setAttachments],
  );

  return { clearSentAttachments, migrateAttachments };
};
